// JWT login/refresh/logout for SPA + Capacitor. Hard auth failures force a clean session end.
import { getAccessToken, setAccessToken, clearAccessToken } from "./token";
import { cacheClearCurrentUser } from "modules/offline/cache";
import {
  isCapacitorClient,
  getStoredRefreshToken,
  setStoredRefreshToken,
  clearStoredRefreshToken,
} from "./refreshStorage";
import { unregisterPushNotifications } from "./pushNotifications";

let refreshPromise = null;
let interceptorsInstalled = false;
let endingSession = false;

export const SESSION_ENDED_EVENT = "lion:session-ended";

const csrfToken = () => document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

const capacitorHeaders = () => {
  const headers = {};
  const csrf = csrfToken();
  if (csrf) headers["X-CSRF-Token"] = csrf;
  if (isCapacitorClient()) headers["X-Lion-Client"] = "capacitor";
  return headers;
};

// Only real CSRF failures — don't scan full HTML (csrf-token meta false-positives).
const isAuthenticityError = (error) => {
  if (error?.response?.status !== 422) return false;
  const data = error.response.data;
  if (_.isString(data)) {
    return /invalid authenticity token|ActionController::InvalidAuthenticityToken/i.test(data);
  }
  const msg = _.toLower([data?.error, data?.message, error?.message].filter(Boolean).join(" "));
  return _.includes(msg, "authenticity") || _.includes(msg, "invalidauthenticitytoken");
};

const hasLocalSession = async () => {
  if (getAccessToken()) return true;
  if (!isCapacitorClient()) return false;
  return !!(await getStoredRefreshToken());
};

// Clear local session. Capacitor: event → router shows Login (no reload loop).
// Caller must only invoke when hadSession — refreshSession may wipe tokens first.
export const forceSessionEnd = async () => {
  if (endingSession) return;
  endingSession = true;

  try {
    await unregisterPushNotifications();
    const stored = isCapacitorClient() ? await getStoredRefreshToken() : null;
    if (stored) {
      try {
        await axios.delete("/auth/logout", {
          headers: capacitorHeaders(),
          params: { refresh_token: stored },
          skipOffline: true,
          validateStatus: () => true,
        });
      } catch (_e) {
        // offline / CORS — local clear still happens
      }
    }
  } finally {
    clearAccessToken();
    await clearStoredRefreshToken();
    await cacheClearCurrentUser();
    if (isCapacitorClient()) {
      window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT));
    } else if (window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
    endingSession = false;
  }
};

export const login = async (email, password) => {
  const { data } = await axios.post(
    "/auth/login",
    { email, password },
    { headers: capacitorHeaders() }
  );
  setAccessToken(data.access_token);
  if (data.refresh_token) {
    await setStoredRefreshToken(data.refresh_token);
  }
  return data.access_token;
};

export const refreshSession = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const body = {};
        if (isCapacitorClient()) {
          const stored = await getStoredRefreshToken();
          if (stored) body.refresh_token = stored;
        }

        const res = await axios.post("/auth/refresh", Object.keys(body).length ? body : null, {
          headers: capacitorHeaders(),
          // cookie path=/auth is sent automatically same-origin (browser)
          validateStatus: (status) => status < 500,
          timeout: 10000,
        });

        if (res.status === 200 && res.data?.access_token) {
          setAccessToken(res.data.access_token);
          if (res.data.refresh_token) {
            await setStoredRefreshToken(res.data.refresh_token);
          }
          return res.data.access_token;
        }

        clearAccessToken();
        // Wipe refresh on hard rejection (incl. CSRF 422) — keep only for network/offline
        if (res.status === 401 || res.status === 403 || res.status === 422) {
          await clearStoredRefreshToken();
        }
        return null;
      } catch (e) {
        clearAccessToken();
        const status = e?.response?.status;
        if (status === 401 || status === 403 || status === 422) {
          await clearStoredRefreshToken();
        }
        // Network / offline failures: keep refresh token so offline session can resume
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
};

export const logout = async () => {
  try {
    await unregisterPushNotifications();
    const params = {};
    if (isCapacitorClient()) {
      const stored = await getStoredRefreshToken();
      if (stored) params.refresh_token = stored;
    }
    await axios.delete("/auth/logout", {
      headers: capacitorHeaders(),
      params: Object.keys(params).length ? params : undefined,
      timeout: 8000,
    });
  } finally {
    clearAccessToken();
    await clearStoredRefreshToken();
    await cacheClearCurrentUser();
  }
};

export const ensureAccessToken = async () => {
  if (getAccessToken()) return getAccessToken();
  return refreshSession();
};

export const installAuthInterceptors = () => {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (isCapacitorClient()) {
      config.headers = config.headers || {};
      config.headers["X-Lion-Client"] = "capacitor";
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      if (!original) return Promise.reject(error);

      // Origin/CSRF mismatch — only kick if we actually had a session
      // (runSync on the login screen must not loop)
      if (isAuthenticityError(error)) {
        if (await hasLocalSession()) await forceSessionEnd();
        return Promise.reject(error);
      }

      // No response = offline / blocked CORS — keep session for offline resume
      if (!error.response || error.response.status !== 401) {
        return Promise.reject(error);
      }

      // Don't loop on auth endpoints
      if (_.includes(original.url, "/auth/login") || _.includes(original.url, "/auth/refresh")) {
        return Promise.reject(error);
      }
      if (original._retry) {
        return Promise.reject(error);
      }
      original._retry = true;
      // Capture before refreshSession wipes tokens on 401
      const hadSession = await hasLocalSession();
      const token = await refreshSession();
      if (!token) {
        if (hadSession) await forceSessionEnd();
        return Promise.reject(error);
      }
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${token}`;
      return axios(original);
    }
  );
};
